import React, {ReactElement} from "react";
import {Splide, SplideSlide, SplideTrack} from "@splidejs/react-splide";

import "@splidejs/react-splide/css/core";

import ModuleBody from "../../layouts/module-body/ModuleBody";
import Card from "../../cards/card/Card";
import Icon from "../../icon/Icon";
import {ImageObject} from "../../image/Image";

import {css, generateKeyId} from "/src/utils/utils";

import CardCarouselArrowIcon from "/src/svg/card-carousel-arrow.svg";

import * as styles from "./card-carousel.module.scss";

const SPLIDE_OPTIONS = {
  perPage: 3,
  rewind: true,
  breakpoints: {
    "767": {
      perPage: 1,
      padding: {
        right: 20,
      },
    },
  },
};

/**
 * Carousel specifically for <Card> components.
 *
 * If you're wondering why this was built instead of reusing the existing
 * carousels (at the point of authoring, 2023/05/21):
 * 1) It uses the Splider library which is a11y friendly.
 * 2) It's visually different.
 * 3) It's structurally different and has to accommodate at least 2 layouts.
 * 4) It's functionally different in having to move 3 cards per page, but then
 *    move 1 card per page on mobile.
 */

const CardCarousel = (
  props: Queries.CardCarouselFragmentFragment
): ReactElement | null => {
  const {title, mosaicLayout, cards} = props;

  if (!cards || !cards.length) {
    return null;
  }

  return (
    <section
      className={css(
        styles.cardCarousel,
        mosaicLayout ? styles.cardCarouselMosaic : ""
      )}
    >
      <ModuleBody classes={styles.cardCarousel__body}>
        <div className={styles.cardCarousel__carousel}>
          {title && (
            <h2 className={css("eyebrow", styles.cardCarousel__title)}>
              {title}
            </h2>
          )}

          <Splide
            className={css(styles.cardCarousel__splide)}
            options={SPLIDE_OPTIONS}
            hasTrack={false}
          >
            <SplideTrack className={css(styles.cardCarousel__splide__track)}>
              {cards.map((card, i) => {
                const key = generateKeyId(
                  `card-carousel-slide-${i}-${card?.title!}`
                );

                return (
                  <SplideSlide
                    key={key}
                    className={styles.cardCarousel__splide__slide}
                  >
                    <Card
                      classes={styles.cardCarousel__splide__card}
                      copy={card?.copy?.copy!}
                      ctas={[card?.ctaLink!]}
                      eyebrow={card?.eyebrow!}
                      headline={card?.title!}
                      image={card?.image as ImageObject}
                    />
                  </SplideSlide>
                );
              })}
            </SplideTrack>

            <div className={styles.cardCarousel__splide__actions}>
              <div className="splide__arrows">
                <button
                  type="button"
                  className={css(
                    styles.cardCarousel__splide__arrow,
                    styles.cardCarousel__splide__arrowPrev,
                    "splide__arrow splide__arrow--prev"
                  )}
                >
                  <Icon
                    classes={styles.cardCarousel__splide__arrowIcon}
                    svg={CardCarouselArrowIcon}
                    role="presentation"
                  />
                </button>

                <button
                  type="button"
                  className={css(
                    styles.cardCarousel__splide__arrow,
                    styles.cardCarousel__splide__arrowNext,
                    "splide__arrow splide__arrow--next"
                  )}
                >
                  <Icon
                    classes={styles.cardCarousel__splide__arrowIcon}
                    svg={CardCarouselArrowIcon}
                    role="presentation"
                  />
                </button>
              </div>

              <ul
                className={css(
                  styles.cardCarousel__splide__pagination,
                  "splide__pagination"
                )}
              />
            </div>
          </Splide>
        </div>
      </ModuleBody>
    </section>
  );
};

export default CardCarousel;
